// WordSearch.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faLightbulb, faCheckCircle, faQuestionCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

export default function WordSearch({ gridSize = 8, words = ["MSME", "FARM", "TECH"], youtubeLink }) {
  const [showHelp, setShowHelp] = useState(false);
  const [notification, setNotification] = useState(null);
  const [grid, setGrid] = useState([]);
  const [foundWords, setFoundWords] = useState([]);

  // Generate a random grid with letters
  const generateGrid = () => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const newGrid = Array(gridSize).fill(null).map(() =>
      Array(gridSize).fill(null).map(() => letters[Math.floor(Math.random() * letters.length)])
    );
    // Place words horizontally for demo
    words.forEach((word, idx) => {
      if (idx < gridSize) {
        for (let i = 0; i < word.length; i++) {
          newGrid[idx][i] = word[i];
        }
      }
    });
    setGrid(newGrid);
  };

  useEffect(() => {
    generateGrid();
  }, []);

  const checkWord = (word) => {
    if (words.includes(word) && !foundWords.includes(word)) {
      setFoundWords([...foundWords, word]);
      setNotification({ type: 'success', message: `✅ Found "${word}"!` });
      setTimeout(() => setNotification(null), 2000);
    } else {
      setNotification({ type: 'error', message: `❌ "${word}" not found` });
      setTimeout(() => setNotification(null), 2000);
    }
  };

  const resetGame = () => {
    setFoundWords([]);
    generateGrid();
    setNotification({ type: 'success', message: "🔄 Game Reset!" });
    setTimeout(() => setNotification(null), 2000);
  };

  return (
    <div className="p-2 min-h-screen bg-black text-gray-300 flex flex-col items-center">
      
      {/* Top Buttons */}
      <div className="w-full flex justify-center gap-4 items-center mb-4">
        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex justify-center items-center gap-2"
          onClick={() => window.history.back()}
        >
          ← Back
        </button>

        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-blue-600 text-black rounded-lg hover:bg-blue-500 flex justify-center items-center gap-2"
          onClick={() => setShowHelp(true)}
        >
          <FontAwesomeIcon icon={faQuestionCircle} /> Help
        </button>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-900 p-6 rounded-xl max-w-lg w-full text-gray-300 flex flex-col gap-4">
            <h3 className="text-m lg:text-xl font-bold text-green-400">📖 How to Play Word Search</h3>
            <p className='text-m lg:text-lg'>
              Find the hidden words in the letter grid. Words can be horizontal, vertical, or diagonal.
            </p>
            <ul className="list-disc list-inside text-gray-400">
              <li>Tap letters in sequence to select a word.</li>
              <li>Press "Check" to validate your selection.</li>
              <li>Use Hint button to reveal a letter.</li>
              <li>Reset to start a new puzzle.</li>
            </ul>
            {youtubeLink && (
              <a
                href={youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-red-600 text-black rounded-lg hover:bg-red-500 text-center font-semibold"
              >
                ▶ Watch
              </a>
            )}
            <button
              className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 mt-2"
              onClick={() => setShowHelp(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg text-sm font-semibold ${
            notification.type === 'error' ? 'bg-red-700 text-white' : 'bg-green-600 text-black'
          } flex justify-between items-center shadow-lg z-50 w-80`}
        >
          <span>{notification.message}</span>
          <button
            className="w-10 p-1 text-lg flex items-center justify-center"
            onClick={() => setNotification(null)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Title */}
      <h2 className="text-lg lg:text-2xl font-bold mb-2 text-green-400">🔤 Word Search</h2>
      <p className="text-gray-400 mb-4 text-center max-w-md text-xs lg:text-m">
        Find the hidden words. Improves attention and vocabulary in a fun way.
      </p>

      {/* Word Grid */}
      <div className="flex flex-col items-center mb-4">
        {grid.map((row, rIdx) => (
          <div key={rIdx} className="flex">
            {row.map((cell, cIdx) => (
              <div
                key={cIdx}
                className="w-12 h-12 border border-gray-600 m-0.5 flex items-center justify-center text-gray-300 font-bold cursor-pointer hover:bg-gray-800 transition"
              >
                {cell}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="mt-2 flex flex-row gap-4 flex-wrap justify-center">
        <button
          onClick={() => checkWord(prompt("Enter word to check:").toUpperCase())}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-green-600 text-black font-semibold rounded-lg hover:bg-green-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faCheckCircle} /> Check
        </button>
        <button
          onClick={resetGame}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-blue-600 text-black font-semibold rounded-lg hover:bg-blue-500 transition"
        >
          🔄 Reset
        </button>
        <button
          onClick={() => {
            // Simple hint: reveal first letter of first unfound word
            const unfound = words.find(w => !foundWords.includes(w));
            if (unfound) {
              setNotification({ type: 'success', message: `Hint: First letter is "${unfound[0]}"` });
              setTimeout(() => setNotification(null), 2000);
            }
          }}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-blue-600 text-black font-semibold rounded-lg hover:bg-blue-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faLightbulb} /> Hint
        </button>
      </div>
    </div>
  );
}
